import { useGetCountryCodesQuery } from "common/types/mainHooks";
import {
  type AccountContactsEdge,
  type AccountEdge,
  type PaymentPrefObjectType,
} from "common/types/federation/supergraphTypes";
import { boolean, object, type AnyObjectSchema, string } from "yup";
import { get, head, mapValues } from "lodash";
import { PaymentPrefType } from "apps/portal/Portal/common/types";
import { type Concrete } from "common/types/typescript";
import { abaChecksum } from "common/utils/stringUtils";
import { PaymentMethod } from "common/types/enums";
import {
  type Address,
  type AddressFieldNames,
  type ExistingPaymentPref,
  type GroupedContact,
  InvestmentAccountActions,
} from "./types";

export const getAccountAddress = (account: AccountEdge) => {
  const { street, street2, street3, city, postalCode, state, region, country } =
    account.node.mailingAddress || {};
  const { objectName: stateName } = state || {};
  const { objectName: countryName } = country || {};

  return {
    street,
    street2,
    street3,
    city,
    postalCode,
    region,
    state: stateName,
    country: countryName,
  };
};

export const defaultFieldNames: Concrete<AddressFieldNames> = {
  street: "street",
  street2: "street2",
  street3: "street3",
  city: "city",
  stateId: "stateId",
  region: "region",
  countryId: "countryId",
  postalCode: "postalCode",
  streetLengthError: "streetLengthError",
  cityStatePostalLengthError: "cityStatePostalLengthError",
};

export const getFieldNames = ({
  fieldNameOverrides,
}: {
  fieldNameOverrides?: AddressFieldNames;
}) =>
  mapValues(defaultFieldNames, (value, key) => {
    return get(fieldNameOverrides, key) || value;
  });

export const checkAddressLength = (address: Address) => {
  const sLength = address.street?.length || 0;
  const s2Length = address.street2 ? address.street2.length + 2 : 0;
  const s3Length = address.street3 ? address.street3.length + 2 : 0;
  return sLength + s2Length + s3Length;
};

export const wireAddressStreetLength = (address: Address, path: string) => {
  return get(address, path, "").length <= 35;
};

export const wireAddressCityPostalStateLength = (address: Address, US_COUNTRY_ID?: string) => {
  if (address.countryId !== US_COUNTRY_ID) {
    return true;
  }
  const cityLength = address.city ? address.city.length : 0;
  const postalLength = address.postalCode ? address.postalCode.length : 0;
  const stateCodeLength = address.stateId ? 2 : 0;
  return 2 + cityLength + postalLength + stateCodeLength <= 35;
};

const addressShape = (
  schema: AnyObjectSchema,
  {
    fieldNameOverrides,
    US_COUNTRY_ID,
  }: {
    fieldNameOverrides?: AddressFieldNames;
    US_COUNTRY_ID: string;
  },
) => {
  const fieldNames = getFieldNames({ fieldNameOverrides });
  return schema.shape(
    {
      [fieldNames.street]: string().trim().required("Required"),
      [fieldNames.street2]: string()
        .trim()
        .when(fieldNames.street3, {
          is: (street3: string | undefined) => !!street3,
          then: (schema) => schema.required("Required when entering three line addresses"),
        }),
      [fieldNames.street3]: string().trim().notRequired(),
      [fieldNames.city]: string().trim().required("Required"),
      [fieldNames.stateId]: string()
        .trim()
        .when(fieldNames.countryId, ([countryId]: string | undefined[], schema) => {
          if (!countryId || countryId.length === 0) {
            return schema.required("Either a state or a country is required");
          }
          if (countryId === US_COUNTRY_ID) {
            return schema.required(
              "Use a U.S. state abbreviation or name (e.g. 'MI' or 'Michigan')",
            );
          }
          return schema;
        }),
      [fieldNames.region]: string().trim(),
      [fieldNames.countryId]: string()
        .trim()
        .when(fieldNames.stateId, {
          is: (stateId: string | undefined) => !stateId || stateId.length === 0,
          then: (schema) => schema.required("Either a state or a country is required"),
        }),
      [fieldNames.postalCode]: string()
        .trim()
        .when(fieldNames.countryId, {
          is: (countryId: string) => countryId === US_COUNTRY_ID,
          then: (schema) => schema.required("Required"),
          otherwise: (schema) => schema.notRequired(),
        }),
    },
    [[fieldNames.stateId, fieldNames.countryId]],
  );
};

export const addressValidation = ({
  fieldNameOverrides,
  US_COUNTRY_ID,
}: {
  fieldNameOverrides?: AddressFieldNames;
  US_COUNTRY_ID: string;
}) => {
  return addressShape(object(), { fieldNameOverrides, US_COUNTRY_ID });
};

export const wireAddressValidation = (US_COUNTRY_ID?: string) =>
  addressValidation({ US_COUNTRY_ID: US_COUNTRY_ID || "" })
    .test(
      "wireStreet1Length",
      "Streets must have 35 characters or fewer",
      function fn(address: Address) {
        if (wireAddressStreetLength(address, "street")) {
          return true;
        }
        return this.createError({
          path: `${this.path}.street`,
          message: "Streets must have 35 characters or fewer",
        });
      },
    )
    .test(
      "wireStreet2Length",
      "Streets must have 35 characters or fewer",
      function fn(address: Address) {
        if (wireAddressStreetLength(address, "street2")) {
          return true;
        }
        return this.createError({
          path: `${this.path}.street2`,
          message: "Streets must have 35 characters or fewer",
        });
      },
    )
    .test(
      "wirePostalCityStateLength",
      "City, state and postal code must be 35 characters or fewer",
      function fn(address: Address) {
        if (wireAddressCityPostalStateLength(address, US_COUNTRY_ID)) {
          return true;
        }
        return this.createError({
          path: `${this.path}.cityStatePostalLengthError`,
          message: "City, state and postal code must be 35 characters or fewer",
        });
      },
    );

const bankShape = (schema: AnyObjectSchema, US_COUNTRY_ID: string) =>
  schema.shape({
    name: string().trim().required("Required"),
    address: wireAddressValidation(US_COUNTRY_ID),
    abaNumber: string()
      .trim()
      .when("address.countryId", {
        is: (countryId: string) => countryId === US_COUNTRY_ID,
        then: (schema) =>
          schema
            .matches(/^[0-9]+$/, "Must be only numbers")
            .max(9, "Must be 9 digits or fewer")
            .required("Required"),
      }),
    swiftCode: string()
      .trim()
      .when("address.countryId", {
        is: (countryId: string) => countryId !== US_COUNTRY_ID,
        then: (schema) =>
          schema
            .matches(/^[a-zA-Z0-9]+$/, "Must contain only alpha-numeric characters")
            .test("swiftLength", "Must be 8 or 11 characters", (swiftCode?: string | null) => {
              return swiftCode?.length === 8 || swiftCode?.length === 11;
            })
            .required("Required"),
      }),
    intlRoutingCode: string().trim(),
  });

const bankValidation = (US_COUNTRY_ID: string) => bankShape(object(), US_COUNTRY_ID);

export const achUpdateValidation = object().shape({
  achUpdate: object().shape({
    bankName: string().trim().required("Required"),
    routingNumber: string()
      .trim()
      .required("Required")
      .matches(/^(0|1|2|3).*$/, "Must begin with 0, 1, or 2")
      .matches(/^[0-9]+$/, "Must be only numbers")
      .min(9, "Must be 9 digits long")
      .max(9, "Must be 9 digits long")
      .test("routingChecksum", "Routing number is invalid", abaChecksum),
    accountNumber: string()
      .trim()
      .max(17, "Must be 17 digits or fewer")
      .matches(/^[-.a-zA-Z0-9]+$/, "Must contain only alpha-numeric values, dashes, or periods")
      .required("Required"),
    accountName: string().trim().required("Required"),
    accountType: string().trim().oneOf(["checking", "savings"]).required("Required"),
    accountOwnership: string().trim().oneOf(["personal", "business"]).required("Required"),
    additionalInstructions: string().trim().max(80, "Must be 80 characters or fewer"),
  }),
});

export const achConfirmValidation = object().shape({
  achConfirm: object().shape({
    fullName: string()
      .trim()
      .matches(/[^\s]+\s+[^\s+]/, "A full name must be provided for ACH signature")
      .required("Your electronic signature is required"),
    acceptance: boolean().oneOf([true], "Please accept the terms of the agreement"),
  }),
});

export const wireUpdateValidation = (US_COUNTRY_ID: string) =>
  object().shape({
    wireUpdate: object().shape({
      beneficiaryName: string()
        .trim()
        .max(35, "Must be 35 characters or fewer")
        .required("Required"),
      beneficiaryAccountNumber: string()
        .trim()
        .max(34, "Must be 34 characters or fewer")
        .required("Required"),
      beneficiaryAddress: wireAddressValidation(US_COUNTRY_ID),
      bank: bankValidation(US_COUNTRY_ID),
      referenceToBeneficiary: string().trim().max(35, "Must be 35 characters or fewer"),
      wireObi: object().shape({
        field0: string().trim().max(35, "Must be 35 characters or fewer"),
        field1: string().trim().max(35, "Must be 35 characters or fewer"),
        field2: string().trim().max(35, "Must be 35 characters or fewer"),
        field3: string().trim().max(35, "Must be 35 characters or fewer"),
      }),
      wireBbi: object().shape({
        field0: string().trim().max(35, "Must be 35 characters or fewer"),
        field1: string().trim().max(35, "Must be 35 characters or fewer"),
        field2: string().trim().max(35, "Must be 35 characters or fewer"),
        field3: string().trim().max(35, "Must be 35 characters or fewer"),
      }),
      intermediary: boolean(),
      intermediaryBank: object()
        .shape({})
        .when("intermediary", {
          is: (intermediary: boolean | undefined) => intermediary,
          then: (schema) => bankShape(schema, US_COUNTRY_ID),
        }),
    }),
  });

export const checkUpdateValidation = (US_COUNTRY_ID: string) =>
  object().shape({
    checkUpdate: object().shape({
      payeeName: string().trim().max(80, "Must be 80 characters or fewer").required("Required"),
      memo: string().trim().max(40, "Must be 40 characters or fewer"),
      useNewAddress: string().trim(),
      address: object()
        .shape({})
        .when("useNewAddress", {
          is: "use_new",
          then: (schema) =>
            addressShape(schema, { US_COUNTRY_ID }).test(
              "streetLength",
              "The length of the street address must be 50 characters or fewer",
              /**
               *
               * For Check address:
               * Length of street + street2 + street3 must be max length 50.
               * If street2 is defined, add 2 characters for concatenation with “, “
               * If street3 is defined, add 2 more for the same.
               * @returns
               */
              function fn(address: Address | undefined | null) {
                if (!address) {
                  return false;
                }
                if (checkAddressLength(address) <= 50) {
                  return true;
                }
                return this.createError({
                  path: `${this.path}.streetLengthError`,
                  message: "The length of the street address must be 50 characters or fewer",
                });
              },
            ),
        }),
    }),
  });

export const existingPaymentValidation = object().shape({
  existing: object().shape({
    existingPaymentMethod: string().trim().required("You must choose a payment method"),
  }),
});

/**
 * Return the last four digits of the account number.
 * If the account number is under eight digits, return the last half of the account number.
 * Behavior is consistent with how the backend redacts account numbers.
 */
export const redactBankInfo = (accountNumber: string) => {
  const toShow = accountNumber.length >= 8 ? -4 : -Math.floor(accountNumber.length / 2);
  return `-${accountNumber.slice(toShow)}`;
};

// Example: Bank name personal checking account ending in -667
const redactAchPayment = (values: any) => {
  const { bankName, accountOwnership, accountType, accountNumber } = values.achUpdate;

  return [
    bankName,
    accountOwnership.toLowerCase(),
    accountType.toLowerCase(),
    redactBankInfo(accountNumber),
  ].join(" ");
};

// Example: Beneficiary bank name account ending in -6456
const redactWirePayment = (values: any) => {
  const bankName = values.wireUpdate.bank.name;
  const accountNumber = redactBankInfo(values.wireUpdate.beneficiaryAccountNumber);

  return `${bankName} account ending in ${accountNumber}`;
};

export const getPaymentMethod = ({
  type,
  values,
  existingPaymentPrefs,
}: {
  type: PaymentPrefType | null;
  values: any;
  existingPaymentPrefs: any;
}) => {
  if (values.existing.existingPaymentMethod !== "new") {
    const existingPaymentPref = existingPaymentPrefs.find(
      (pref: any) => pref.id === values.existing.existingPaymentMethod,
    );

    if (existingPaymentPref.bankInfoRedacted) {
      return {
        bankInfoRedacted: existingPaymentPref.bankInfoRedacted,
        paymentMethod: type,
      };
    }
    return {
      paymentMethod: type,
      address: {
        street: existingPaymentPref.checkAddrStreet,
        street2: existingPaymentPref.checkAddrStreet2,
        street3: existingPaymentPref.checkAddrStreet3,
        city: existingPaymentPref.checkAddrCity,
        region: existingPaymentPref.checkAddrRegion,
        postalCode: existingPaymentPref.checkAddrPostalCode,
        state: existingPaymentPref.checkAddrState?.name ?? "",
        country: existingPaymentPref.checkAddrCountry?.name ?? "",
      },
    };
  }

  switch (type) {
    case PaymentPrefType.ACH:
      return { paymentMethod: type, bankInfoRedacted: redactAchPayment(values) };
    case PaymentPrefType.Wire:
      return {
        paymentMethod: type,
        bankInfoRedacted: redactWirePayment(values),
        address: values.wireUpdate.bankAddress,
      };
    case PaymentPrefType.Check:
      return {
        paymentMethod: type,
        address: values.checkUpdate.address,
      };
    default:
      return {};
  }
};

export const useGetUSCountryId = () => {
  const { data } = useGetCountryCodesQuery();
  return {
    US_COUNTRY_ID: data?.countries?.edges
      ? head(
          data.countries.edges
            .filter((country) => country.node.alpha2code === "US")
            .map((country) => country.node.id),
        )
      : undefined,
  };
};

export const getPaymentPrefLabel = (paymentPrefType?: string) => {
  switch (paymentPrefType) {
    case PaymentPrefType.ACH:
      return "ACH Direct Deposit";
    case PaymentPrefType.Wire:
      return "Wire Transfer";
    case PaymentPrefType.Check:
      return "Check";
    case PaymentPrefType.EFT:
      return "Canada bank transfer (EFT)";
    case PaymentPrefType.SEPA:
      return "Europe bank transfer (SEPA)";
    case PaymentPrefType.BECS:
      return "Australia bank transfer (BECS)";
    case PaymentPrefType.BACS:
      return "United Kingdom bank transfer (BACS)";
    default:
      return "";
  }
};

export const processPaymentPref = (paymentPref?: PaymentPrefObjectType) => {
  const {
    paymentMethod,
    bankInfoRedacted,
    checkAddrCity,
    checkAddrCountry,
    checkAddrState,
    checkAddrStreet,
    checkAddrStreet2,
    checkAddrStreet3,
    checkAddrRegion,
    checkAddrPostalCode,
    checkMailingAddressSameAsAccount,
  } = paymentPref || {};
  const commonPaymentData = { id: paymentPref?.id || "", paymentMethod };
  if (paymentMethod?.toLowerCase() === PaymentMethod.Check.toLowerCase()) {
    const { name: state } = checkAddrState || {};
    const { name: country } = checkAddrCountry || {};
    return {
      ...commonPaymentData,
      bankInfoRedacted,
      checkMailingAddressSameAsAccount,
      address: {
        street: checkAddrStreet,
        street2: checkAddrStreet2,
        street3: checkAddrStreet3,
        city: checkAddrCity,
        state,
        country,
        region: checkAddrRegion,
        postalCode: checkAddrPostalCode,
      },
    };
  }
  return { ...commonPaymentData, bankInfoRedacted };
};

export const getPaymentPrefs = (account: AccountEdge) =>
  account.node.paymentPrefs?.map((paymentPref) =>
    processPaymentPref(paymentPref),
  ) as ExistingPaymentPref[];

export const fetchPositionNamesFromAccounts = (accounts: AccountEdge[]) => {
  return accounts
    .flatMap((account) =>
      account.node.positions?.edges.map((position) => position.node.investmentEntity.name),
    )
    .filter((positionName) => !!positionName) as string[];
};

export const getUniqueAccountAndArenaIds = (
  accounts: AccountEdge[],
): { accountIds: string[]; arenaIds: string[] } => {
  const accountIds = new Set();
  const arenaIds = new Set();

  accounts.forEach((account) => {
    if (account.node.id && account.node.arenaId) {
      accountIds.add(account.node.id);
      arenaIds.add(account.node.arenaId);
    }
  });

  return {
    accountIds: Array.from(accountIds) as string[],
    arenaIds: Array.from(arenaIds) as string[],
  };
};

function sortUniqueContacts(
  uniqueContacts: GroupedContact[],
  currentUserEmailAddress: string | undefined,
) {
  return uniqueContacts.sort((a, b): number => {
    const aEmailMatches = a.emailAddress === currentUserEmailAddress;
    const bEmailMatches = b.emailAddress === currentUserEmailAddress;

    if (aEmailMatches && !bEmailMatches) {
      return -1;
    }
    if (!aEmailMatches && bEmailMatches) {
      return 1;
    }

    const lengthDiff = b.presentInAccounts.length - a.presentInAccounts.length;
    if (lengthDiff !== 0) {
      return lengthDiff;
    }
    // we need to sort only on the basis of first name in displayName array because displayName array is already sorted
    return a.displayNameArray[0].localeCompare(b.displayNameArray[0]);
  });
}

function getUniqueDisplayNameArrayForGroupedContacts(
  contactWithPortalAccount: AccountContactsEdge[],
  associatedContacts: AccountContactsEdge[],
) {
  const displayNameArray: string[] = [];
  if (contactWithPortalAccount.length && !displayNameArray.length) {
    // we want only one displayName for the contact with portal account
    const { contactDetails } = contactWithPortalAccount[0].node;
    displayNameArray.push(contactDetails?.portalFullName || "");
  } else {
    associatedContacts.forEach((contact) => {
      const { contactDetails } = contact.node;
      const portalFullName = contactDetails?.portalFullName || "";
      if (portalFullName && !displayNameArray.includes(portalFullName)) {
        displayNameArray.push(portalFullName);
      }
    });
    displayNameArray.sort((a, b) => a.localeCompare(b)); // sort dislplayname array in ascending order
  }
  return displayNameArray;
}

export const getGroupedContacts = (
  accountsData: AccountContactsEdge[] | undefined,
  currentUserEmailAddress: string | undefined,
) => {
  const contactsMap = new Map<string, AccountContactsEdge[]>();
  const accountsWithoutEmail: AccountContactsEdge[] = [];
  const uniqueContacts: GroupedContact[] = [];

  accountsData?.forEach((account) => {
    const { accountName, contactDetails } = account.node;
    const email = contactDetails?.primaryEmail?.emailAddress;

    if (!email) {
      accountsWithoutEmail.push(account);
    } else if (accountName && email) {
      if (!contactsMap.has(email)) {
        contactsMap.set(email, [account]);
      } else {
        contactsMap.get(email)?.push(account);
      }
    }
  });

  if (accountsWithoutEmail.length > 0) {
    accountsWithoutEmail.forEach((account) => {
      const displayName = [account.node.contactDetails?.portalFullName || ""]; // if emailid is not present - displayName will be associated with the account name
      const presentInAccounts = [account.node.accountName];

      uniqueContacts.push({
        id: Math.random().toString(36).substr(2, 9),
        emailAddress: "",
        displayNameArray: displayName,
        presentInAccounts,
        associatedContacts: [account],
      });
    });
  }

  contactsMap.forEach((associatedContacts, email) => {
    // const displayName = associatedContacts[0].node.contactDetails?.portalFullName || "";
    const contactWithPortalAccount = associatedContacts.filter(
      (associatedContact) => associatedContact.node.hasPortalAccount,
    );
    const displayNameArray = getUniqueDisplayNameArrayForGroupedContacts(
      contactWithPortalAccount,
      associatedContacts,
    );
    const presentInAccounts = associatedContacts.map((contact) => contact.node.accountName);

    uniqueContacts.push({
      id: Math.random().toString(36).substr(2, 9),
      emailAddress: email,
      displayNameArray,
      presentInAccounts,
      associatedContacts,
    });
  });

  return sortUniqueContacts(uniqueContacts, currentUserEmailAddress);
};

export const searchContactsInGroupedContactList = (
  searchedContacts: GroupedContact[],
  groupedContacts: GroupedContact[],
  query: string,
) => {
  if (query.length < 2) {
    if (searchedContacts.length < groupedContacts.length) {
      return groupedContacts;
    }
    // we return here to avoid setting grouped contacts everytime character is typed in the searchbar.
    // We explicitly reset the searched contacts only once to grouped contacts when the query is less than 3 characters
    // eslint-disable-next-line consistent-return
    return;
  }
  return groupedContacts.filter(
    (contact) =>
      contact.displayNameArray.some((displayName) =>
        displayName.toLowerCase().includes(query.toLowerCase()),
      ) || contact.emailAddress.toLowerCase().includes(query.toLowerCase()),
  );
};

export const getContactListFromGroupedContacts = (
  keys: string[],
  groupedContacts: GroupedContact[],
): {
  contactList: AccountContactsEdge[];
  selectedContact: AccountContactsEdge | undefined;
} => {
  const contactList: AccountContactsEdge[] = [];
  groupedContacts.forEach((groupedContact) => {
    if (keys.includes(groupedContact.id)) {
      // we know for sure that associatedContacts here are AccountContactsEdge[] hence we can safely cast it
      contactList.push(
        ...(groupedContact.associatedContacts as unknown as AccountContactsEdge[]),
      );
    }
  });
  return {
    contactList,
    selectedContact: head(contactList), // TODO - This will change once we have information of contact associated with the portal account, till then we are using the first contact of the list
  };
};

export const fetchEnabledAndDisabledFromAccounts = (
  action: InvestmentAccountActions,
  accounts: AccountEdge[],
) => {
  let disabledAccounts: AccountEdge[] = [];
  let enabledAccounts: AccountEdge[] = [];

  if (
    action === InvestmentAccountActions.UpdateAddress ||
    action === InvestmentAccountActions.UpdatePayment
  ) {
    disabledAccounts = accounts.filter((account) => !account.node.canEditAccountDetails);
    enabledAccounts = accounts.filter((account) => account.node.canEditAccountDetails);
  } else if (
    action === InvestmentAccountActions.UpdateContactCommunicationPreferences ||
    action === InvestmentAccountActions.RemoveContacts ||
    action === InvestmentAccountActions.AddContact
  ) {
    disabledAccounts = accounts.filter((account) => !account.node.canLpcmManageContacts);
    enabledAccounts = accounts.filter((account) => account.node.canLpcmManageContacts);
  }

  return {
    disabledAccounts,
    enabledAccounts,
  };
};
